import { useEffect, useState } from "react"

/** Within this many pixels of the end, the page counts as scrolled to the
 * bottom. Browser zoom rounds the viewport and page heights separately and
 * snaps the maximum offset to device pixels, so an exact match can miss. */
const PAGE_END_TOLERANCE_PX = 2

const isScrolledToPageEnd = (): boolean => {
  // clientHeight, unlike innerHeight, leaves out a horizontal scrollbar.
  const { clientHeight, scrollHeight } = document.documentElement
  return (
    scrollHeight > clientHeight &&
    window.scrollY + clientHeight >= scrollHeight - PAGE_END_TOLERANCE_PX
  )
}

/** The tracked section the URL fragment names, if any. Section ids are plain
 * ASCII, so the fragment is compared as-is: decoding a malformed one such as
 * `#%` would throw. */
const fragmentSection = (ids: readonly string[]): string | null => {
  const fragment = window.location.hash.slice(1)
  return ids.includes(fragment) ? fragment : null
}

/** Keys that scroll the page. Any other key, such as Tab or Shift, leaves a
 * pin in place. */
const SCROLL_KEYS = new Set([
  `ArrowUp`,
  `ArrowDown`,
  `PageUp`,
  `PageDown`,
  `Home`,
  `End`,
  ` `,
])

/** Id of the section currently crossing the upper-middle band of the
 * viewport, or null before the first section scrolls into that band.
 *
 * With `lastAtPageEnd`, the last id is active whenever the page is scrolled
 * to the bottom. A page that ends on a short section can never lift that
 * section's top into the band, so without this its dot would never light.
 * On such a page a link to an earlier section can land at the bottom too, so
 * the section a link was followed to stays active until the reader scrolls
 * by hand or another section crosses the band.
 *
 * Change `observeKey` when a section's node is replaced under the same id,
 * so the observers follow the new node without losing this state. */
export function useActiveSection(
  ids: readonly string[],
  {
    lastAtPageEnd = false,
    observeKey = ``,
  }: { readonly lastAtPageEnd?: boolean; readonly observeKey?: string } = {},
): string | null {
  const [crossing, setCrossing] = useState<string | null>(null)
  const [atPageEnd, setAtPageEnd] = useState(false)
  // Seeded once from the URL, so a reload at a fragment keeps the section it
  // restores to. If the page is not there, the band's first report names
  // another section and releases the pin.
  const [followed, setFollowed] = useState(() =>
    lastAtPageEnd ? fragmentSection(ids) : null,
  )
  const idsKey = ids.join(`,`)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const { id } = entry.target
            setCrossing(id)
            setFollowed((pinned) => (pinned === id ? pinned : null))
          }
        }
      },
      // Band between 30% and 40% of viewport height, so exactly one section
      // reads as current even when several are on screen.
      { rootMargin: `-30% 0px -60% 0px` },
    )
    for (const id of idsKey.split(`,`)) {
      const el = document.getElementById(id)
      if (el) {
        observer.observe(el)
      }
    }
    return () => observer.disconnect()
  }, [idsKey, observeKey])

  useEffect(() => {
    if (!lastAtPageEnd) {
      return
    }
    const update = (): void => setAtPageEnd(isScrolledToPageEnd())
    update()
    window.addEventListener(`scroll`, update, { passive: true })
    window.addEventListener(`resize`, update)
    // A read answering or a chart laying out can grow the page under a
    // reader parked at the bottom, with no scroll to say so. The sections
    // are observed because body stays one viewport tall (see global.css).
    const resizeObserver = new ResizeObserver(update)
    for (const id of idsKey.split(`,`)) {
      const el = document.getElementById(id)
      if (el) {
        resizeObserver.observe(el)
      }
    }
    return () => {
      window.removeEventListener(`scroll`, update)
      window.removeEventListener(`resize`, update)
      resizeObserver.disconnect()
    }
  }, [lastAtPageEnd, idsKey, observeKey])

  useEffect(() => {
    if (!lastAtPageEnd) {
      return
    }
    const sectionIds = idsKey.split(`,`)
    // Runs before the browser follows the link, so the pin is in place when
    // the jump's scroll lands at the page end.
    const follow = (event: MouseEvent): void => {
      // A modified or non-primary click opens the link elsewhere, and a
      // prevented one goes nowhere; either way this page does not move.
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return
      }
      const link =
        event.target instanceof Element
          ? event.target.closest(`a[href^="#"]`)
          : null
      const id = link?.getAttribute(`href`)?.slice(1)
      if (id && sectionIds.includes(id)) {
        setFollowed(id)
      }
    }
    // Back and Forward between fragments fire no click and, on a short page,
    // may not scroll either; the fragment then names the section to show.
    const followFragment = (): void => setFollowed(fragmentSection(sectionIds))
    // Scrolling by hand ends a pin.
    const release = (): void => setFollowed(null)
    const releaseOnScrollKey = (event: KeyboardEvent): void => {
      if (SCROLL_KEYS.has(event.key)) {
        release()
      }
    }
    document.addEventListener(`click`, follow)
    window.addEventListener(`hashchange`, followFragment)
    window.addEventListener(`wheel`, release, { passive: true })
    window.addEventListener(`touchmove`, release, { passive: true })
    window.addEventListener(`keydown`, releaseOnScrollKey)
    return () => {
      document.removeEventListener(`click`, follow)
      window.removeEventListener(`hashchange`, followFragment)
      window.removeEventListener(`wheel`, release)
      window.removeEventListener(`touchmove`, release)
      window.removeEventListener(`keydown`, releaseOnScrollKey)
    }
  }, [lastAtPageEnd, idsKey])

  if (!lastAtPageEnd) {
    return crossing
  }
  // A pin to a section whose read came back empty points at a link that is
  // gone. Clear it during render rather than hide it, so a refetch that
  // brings the section back does not revive a pin the reader never renewed.
  const pinGone = followed !== null && !ids.includes(followed)
  if (pinGone) {
    setFollowed(null)
  }
  const last = ids.at(-1)
  return (
    (pinGone ? null : followed) ??
    (atPageEnd && last !== undefined ? last : crossing)
  )
}
